import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  Paper,
} from '@mui/material';
import moment from 'moment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../common/components/LocalizationProvider';
import {
  Signal,
  GPS,
  Power,
  Battery,
} from '../common/components/CommonObjects';
import { getSignal } from '../common/components/CommonValues';
import CameraDialog from './CameraDialog';

export const verifyInstallation = (position) => {
  if (!position) return false;
  if (!position.attributes.hasOwnProperty('rssi')) return false;
  if (!position.attributes.hasOwnProperty('hdop')) return false;
  if (!position.attributes.hasOwnProperty('power')) return false;

  const signal = getSignal(position.attributes.rssi);
  const { hdop, power } = position.attributes;

  return power > 7 && hdop <= 10 && hdop > 0 && signal >= 2;
};

const InstallPage = () => {
  const t = useTranslation();
  const [imei, setImei] = useState('');
  const [vin, setVin] = useState('');
  const [vehicleImage, setVehicleImage] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [fieldToScan, setFieldToScan] = useState(null);
  const [position, setPosition] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [retryCountdown, setRetryCountdown] = useState(0);
  const navigate = useNavigate();
  const retryTimer = useRef(null);

  useEffect(() => () => retryTimer.current && clearInterval(retryTimer.current), []);

  const handleBack = () => {
    navigate('/');
  };

  const handleOpenCamera = async (field) => {
    // Attempt to access the camera
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });

    // If successful, open the camera dialog
    if (stream) {
      setFieldToScan(field);
      setCameraOpen(true);
    }
  };

  const handleCaptureResult = (field, result) => {
    if (field === 'imei') setImei(result);
    if (field === 'vin') setVin(result);
    if (field === 'vehicleImage') setVehicleImage(result);
  };

  const verifyDevice = async () => {
    setVerifying(true); // Show "Verifying" message immediately
    try {
      // Create FormData to handle both text and binary data
      const formData = new FormData();
      formData.append('uniqueid', imei);
      formData.append('vin', vin);

      // If we have a vehicle image, add it to the form data
      if (vehicleImage) {
        // Convert base64 to blob
        const response = await fetch(vehicleImage);
        const blob = await response.blob();
        formData.append('vehicleImage', blob, 'vehicle-image.png');
      }

      const response = await fetch('/api/devices/verify', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const verifiedPosition = await response.json();
        setPosition(verifiedPosition);
        setVerifying(false);
        setRetryCountdown(0); // Stop retry logic
        clearInterval(retryTimer.current);
        retryTimer.current = null;
      } else {
        setVerifying(false); // Switch to retry mode
        setRetryCountdown(10); // Start retry countdown
        if (!retryTimer.current) {
          retryTimer.current = setInterval(() => {
            setRetryCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(retryTimer.current);
                retryTimer.current = null;
                verifyDevice(); // Retry verification
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }
      }
    } catch (e) {
      setVerifying(false); // Stop "Verifying" message
      throw new Error('Communication Error');
    }
  };

  const handleSubmit = () => {
    setVerifying(true);
    verifyDevice();
  };

  const handleCancelVerification = () => {
    setVerifying(false);
    setRetryCountdown(0);
    if (retryTimer.current) {
      clearInterval(retryTimer.current);
      retryTimer.current = null;
    }
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" padding={3}>
      <Box display="flex" justifyContent="flex-start" width="100%" mb={2}>
        <IconButton onClick={handleBack}>
          <ArrowBackIcon />
        </IconButton>
      </Box>
      <TextField
        label={t('deviceIMEI')}
        value={imei}
        onChange={(e) => setImei(e.target.value)}
        fullWidth
        margin="normal"
        InputProps={{
          endAdornment: (
            <IconButton onClick={() => handleOpenCamera('imei')} aria-label="Open Camera for IMEI">
              <PhotoCameraIcon />
            </IconButton>
          ),
        }}
      />
      <TextField
        label={t('vehicleVIN')}
        value={vin}
        onChange={(e) => setVin(e.target.value)}
        fullWidth
        margin="normal"
        InputProps={{
          endAdornment: (
            <IconButton onClick={() => handleOpenCamera('vin')} aria-label="Open Camera for VIN">
              <PhotoCameraIcon />
            </IconButton>
          ),
        }}
      />
      {/* Vehicle Image Section */}
      <Box sx={{ width: '100%', mt: 2 }}>
        <TextField
          label={t('vehicleImage') || 'Vehicle Image'}
          value=""
          disabled
          fullWidth
          margin="normal"
          InputProps={{
            endAdornment: (
              <IconButton
                onClick={() => handleOpenCamera('vehicleImage')}
                aria-label="Capture Vehicle Image"
                color="primary"
              >
                <PhotoCameraIcon />
              </IconButton>
            ),
          }}
          helperText={!vehicleImage && 'Tap the camera icon to capture a vehicle image'}
        />

        {vehicleImage && (
          <Paper
            elevation={2}
            sx={{
              mt: 1,
              p: 1,
              display: 'flex',
              justifyContent: 'center',
              borderRadius: 2,
            }}
          >
            <img
              src={vehicleImage}
              alt="Vehicle"
              style={{
                width: '100%',
                maxHeight: '180px',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
          </Paper>
        )}
      </Box>

      <Box display="flex" alignItems="center" gap={2} mt={2}>
        {verifying ? (
          <Typography variant="body1" color="primary">
            {t('sharedVerifying')}
          </Typography>
        ) : retryCountdown > 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
            <Button
              variant="contained"
              color="error"
              onClick={handleCancelVerification}
            >
              {t('sharedCancel')}
            </Button>
            <Typography variant="body1" color="error">
              {t('deviceNotInstalledRetry').replace('{{seconds}}', retryCountdown)}
            </Typography>
          </Box>
        ) : position ? (
          <Button variant="contained" color={verifyInstallation(position) ? 'success' : 'error'}>
            {t('sharedInstalled')}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            fullWidth
            disabled={!imei}
          >
            {t('sharedVerify')}
          </Button>
        )}
      </Box>
      <CameraDialog
        open={cameraOpen}
        field={fieldToScan}
        imageOnly={fieldToScan === 'vehicleImage'}
        onClose={() => setCameraOpen(false)}
        onCapture={handleCaptureResult}
      />
      {position && (
        <Box mt={2} display="flex" flexDirection="column" justifyContent="center" alignItems="center" width="100%">
          <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2}>
            {position.attributes.hasOwnProperty('rssi') && <Signal t={t} signal={getSignal(position.attributes.rssi)} />}
            {position.attributes.hasOwnProperty('hdop') && <GPS t={t} hdop={position.attributes.hdop} />}
            {position.attributes.hasOwnProperty('power') && <Power t={t} power={position.attributes.power} />}
            {position.attributes.hasOwnProperty('batteryLevel') && (
              <Battery t={t} batteryLevel={position.attributes.batteryLevel} charging={position.attributes.charge} />
            )}
          </Box>
          <Box mt={2}>
            <Typography variant="body2">
              {`${t('deviceLastUpdate')} ${moment(position.deviceTime).fromNow()}`}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default InstallPage;
